import React from 'react';

export class Helpers {
    /**
     * Safely parses HTML string and converts it to React elements, allowing only specific tags
     * This prevents XSS attacks by filtering out dangerous tags and attributes
     *
     * @param html - The HTML string to parse
     * @param allowedTags - Array of allowed HTML tag names (default: ['a', 'strong', 'em', 'div', 'span', 'section', 'br', 'img'])
     * @returns Array of React nodes representing the safe HTML content
     */
    public static parseAllowedHtml(
        html: string,
        allowedTags: string[] = [
            // Basic structural elements
            'a',
            'div',
            'span',
            'section',
            'br',
            'hr',
            'p',

            // Headings
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',

            // Text formatting
            'strong',
            'b',
            'em',
            'i',
            'u',
            'small',
            'sup',
            'sub',
            'mark',
            'del',
            'ins',

            // Lists
            'ul',
            'ol',
            'li',

            // Media
            'img',

            // Semantic elements
            'blockquote',
            'cite',
            'abbr',
        ]
    ): React.ReactNode[] {
        // Use document fragment for better performance
        const temp = document.createDocumentFragment();
        const container = document.createElement('div');
        container.innerHTML = html;
        temp.appendChild(container);

        // Cache allowed tags set for O(1) lookup
        const allowedTagsSet = new Set(allowedTags);

        function walk(node: ChildNode): React.ReactNode {
            if (node.nodeType === Node.TEXT_NODE) {
                return node.textContent;
            }
            if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                const tag = el.tagName.toLowerCase();
                if (allowedTagsSet.has(tag)) {
                    const children = Array.from(el.childNodes).map((child, index) => {
                        const walkResult = walk(child);
                        // Add key to React elements
                        if (React.isValidElement(walkResult)) {
                            return React.cloneElement(walkResult, {key: index});
                        }
                        return walkResult;
                    });
                    if (tag === 'a') {
                        // Only allow safe attributes for <a>
                        const href = el.getAttribute('href');
                        const safeHref =
                            href &&
                            (() => {
                                const decodedHref = decodeURI(href).trim().toLowerCase();
                                return decodedHref.startsWith('javascript:') ||
                                    decodedHref.startsWith('data:') ||
                                    decodedHref.startsWith('vbscript:')
                                    ? undefined
                                    : href;
                            })();
                        return React.createElement(
                            'a',
                            {
                                href: safeHref,
                                target: el.getAttribute('target') || undefined,
                                rel:
                                    el.getAttribute('rel') ||
                                    (el.getAttribute('target') === '_blank'
                                        ? 'noopener noreferrer'
                                        : undefined),
                                className: el.getAttribute('class') || undefined,
                                title: el.getAttribute('title') || undefined,
                                alt: el.getAttribute('alt') || undefined,
                            },
                            ...children
                        );
                    }

                    if (tag === 'img') {
                        // Only allow safe attributes for <img>
                        const src = el.getAttribute('src');
                        const safeSrc =
                            src &&
                            (() => {
                                const decodedSrc = decodeURI(src).trim().toLowerCase();
                                return decodedSrc.startsWith('javascript:') ||
                                    decodedSrc.startsWith('data:') ||
                                    decodedSrc.startsWith('vbscript:')
                                    ? undefined
                                    : src;
                            })();

                        // Parse width and height attributes safely
                        const parseNumericAttribute = (attr: string | null): number | undefined => {
                            if (!attr) return undefined;
                            const num = parseInt(attr, 10);
                            return isNaN(num) || num < 0 ? undefined : num;
                        };

                        return React.createElement('img', {
                            src: safeSrc,
                            alt: el.getAttribute('alt') || '',
                            title: el.getAttribute('title') || undefined,
                            width: parseNumericAttribute(el.getAttribute('width')),
                            height: parseNumericAttribute(el.getAttribute('height')),
                            className: el.getAttribute('class') || undefined,
                            id: el.getAttribute('id') || undefined,
                        });
                    }

                    // For other tags, preserve safe attributes
                    const props: any = {};

                    // Preserve class attribute as className for React
                    const className = el.getAttribute('class');
                    if (className) {
                        props.className = className;
                    }

                    // Preserve id attribute
                    const id = el.getAttribute('id');
                    if (id) {
                        props.id = id;
                    }

                    // Preserve title attribute
                    const title = el.getAttribute('title');
                    if (title) {
                        props.title = title;
                    }

                    // Preserve alt attribute
                    const alt = el.getAttribute('alt');
                    if (alt) {
                        props.alt = alt;
                    }

                    // Preserve data attributes for React components
                    Array.from(el.attributes).forEach(attr => {
                        if (attr.name.startsWith('data-')) {
                            props[attr.name] = attr.value;
                        }
                    });

                    return React.createElement(tag, props, ...children);
                } else {
                    // Disallow tag, render its children as plain text
                    return Array.from(el.childNodes).map((child, index) => {
                        const walkResult = walk(child);
                        // Add key to React elements for disallowed tags too
                        if (React.isValidElement(walkResult)) {
                            return React.cloneElement(walkResult, {key: index});
                        }
                        return walkResult;
                    });
                }
            }
            return null;
        }

        return Array.from(container.childNodes).map((child, index) => {
            const walkResult = walk(child);
            // Add key to top-level React elements
            if (React.isValidElement(walkResult)) {
                return React.cloneElement(walkResult, {key: index});
            }
            return walkResult;
        });
    }

    /**
     * Sanitizes a string for safe logging to prevent log injection attacks.
     * This function removes or escapes dangerous characters that could manipulate
     * terminal output, forge log entries, or execute commands.
     *
     * @since 17.6.0
     * @param {string} value - The value to sanitize for logging.
     * @returns {string} Safe string for logging.
     */
    public static sanitizeForLogging(value: string): string {
        if (!value) return '[empty]';

        // Replace control characters, escape sequences, and other dangerous characters
        return (
            value
                .replace(/[\x00-\x1F\x7F-\x9F]/g, '�') // Replace control characters
                .replace(/\\/g, '\\\\') // Escape backslashes
                .replace(/"/g, '\\"') // Escape quotes
                .replace(/\n/g, '\\n') // Escape newlines
                .replace(/\r/g, '\\r') // Escape carriage returns
                .replace(/\t/g, '\\t') // Escape tabs
                .substring(0, 100) + (value.length > 100 ? '...[truncated]' : '')
        ); // Truncate long values
    }

    /**
     * Sanitizes image src URLs to prevent XSS attacks
     * Only allows certain URL schemes and validates data URLs for images
     *
     * @param src - The image src URL to sanitize
     * @returns Sanitized URL or empty string if blocked
     */
    public static sanitizeImageSrc(src: string): string {
        if (!src) return '';

        try {
            // Allow: http, https, data (raster images), protocol-relative URL, relative path
            const allowedProtocols = ['http:', 'https:', 'data:', ''];
            const url = new URL(src, window.location.origin);

            // If data: URL, only allow raster image types (PNG, JPEG, GIF, BMP, WEBP)
            if (url.protocol === 'data:') {
                // Only allow base64-encoded raster image data, and explicitly block SVG
                const allowedMimeTypes = [
                    'image/png',
                    'image/jpeg',
                    'image/jpg',
                    'image/gif',
                    'image/bmp',
                    'image/webp',
                ];
                // Extract MIME type and base64 data from data URI
                const match = src.match(/^data:([^;]+);base64,([A-Za-z0-9+/=]+)$/i);
                if (!match || !allowedMimeTypes.includes(match[1].toLowerCase())) {
                    return '';
                }
                // Validate base64 data only contains allowed characters
                if (!/^(?:[A-Za-z0-9+/=]|\s)*$/.test(match[2])) {
                    return '';
                }
                // For data: URIs, also check for forbidden characters anywhere
                if (
                    /[\x00-\x1F\x7F-\x9F"'<>`\\]/.test(src) || // Control chars, quotes, angle brackets, backslash, backtick
                    /[\u2028\u2029]/.test(src) // Line/para separators
                ) {
                    return '';
                }
                // Reconstruct safe data URI, using lowercased MIME type
                return `data:${match[1].toLowerCase()};base64,${match[2]}`;
            }

            if (allowedProtocols.includes(url.protocol)) {
                // Ensure the result does not include forbidden characters (defense-in-depth)
                const cleanHref = url.href;
                if (
                    /[\x00-\x1F\x7F-\x9F"'<>`\\]/.test(cleanHref) ||
                    /[\u2028\u2029]/.test(cleanHref)
                ) {
                    return '';
                }
                return cleanHref;
            }
            // reject javascript:, vbscript:, etc
            return '';
        } catch (err) {
            // fallback: block broken URLs
            return '';
        }
    }
    /**
     * Escapes a string for safe insertion into an HTML attribute value
     * Converts &, <, >, ", ', and ` to HTML entities
     */
    public static escapeHtmlAttribute(attr: string): string {
        return String(attr)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/`/g, '&#96;');
    }
}
